import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { EMPTY_VIEW_ICONS, EmptyView } from '@shared/components/ui/EmptyView';
import { SectionBox } from '@shared/components/ui/SectionBox';
import { TotalResults } from '@shared/components/ui/TotalResults';
import { useCustomerReportData, useCustomerExport, useErrorToast, useCustomerFilters } from '../../hooks';
import { CustomerTable } from '../display';
import { CustomerFilters, ExportButton } from '../interaction';
import { formatLocalDateYMD } from '@shared/lib/formatLocalDate';

export const CustomerMain = () => {
  const { t } = useTranslation('marketingModule');
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const { showErrorToast, showExportSuccessToast, showExportErrorToast } = useErrorToast();
  const {
    ordersMinMonth,
    ordersMaxMonth,
    saleType,
    countryId,
    customerOrigins,
    sortColumn,
    sortDirection,
    customerFilters,
    hasActiveFilters,
    updateFilters,
    updatePagination,
    updateSort,
    resetFilters,
  } = useCustomerFilters();

  const { customers, paginationMeta, isLoading, isFetching, error } = useCustomerReportData({
    filters: customerFilters,
  });

  const exportHook = useCustomerExport();

  useEffect(() => {
    showErrorToast(error);
  }, [error, showErrorToast]);

  useEffect(() => {
    if (isInitialLoad && !isLoading) {
      setIsInitialLoad(false);
    }
  }, [isLoading, isInitialLoad]);

  const handleExport = useCallback(async () => {
    try {
      await exportHook.handleExport(customerFilters, `customer-report-${formatLocalDateYMD(new Date())}.xlsx`);
      showExportSuccessToast();
    } catch {
      showExportErrorToast();
    }
  }, [customerFilters, exportHook, showExportSuccessToast, showExportErrorToast]);

  const hasErrorNoData = error && !customers?.length;
  const showTable = !hasErrorNoData && (customers?.length > 0 || hasActiveFilters);

  return (
    <SectionBox data-testid="marketing-customers-section">
      <div className="space-y-5">
        <h2 className="text-lg font-semibold text-title">{t('customers.title', 'Listado de Clientes')}</h2>

        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2 flex-wrap">
            <CustomerFilters
              ordersMinMonth={ordersMinMonth}
              ordersMaxMonth={ordersMaxMonth}
              saleType={saleType}
              countryId={countryId}
              customerOrigins={customerOrigins}
              updateFilters={updateFilters}
              onClear={resetFilters}
            />
            {customers?.length > 0 && !error && (
              <ExportButton onExport={handleExport} isExporting={exportHook.isExporting} disabled={isLoading} />
            )}
          </div>
          {paginationMeta && paginationMeta.total > 0 && <TotalResults total={paginationMeta.total} />}
        </div>

        {hasErrorNoData ? (
          <EmptyView
            type="emptyState"
            icon={EMPTY_VIEW_ICONS.ORDERS}
            title={t('empty.error.title', 'Error al cargar datos')}
            description={t(
              'empty.error.description',
              'No se pudieron cargar los datos del reporte. Por favor, intenta de nuevo.',
            )}
          />
        ) : !customers?.length && !hasActiveFilters ? (
          <EmptyView
            type="emptyState"
            icon={EMPTY_VIEW_ICONS.ORDERS}
            title={t('empty.no_data.title', 'No hay clientes disponibles')}
            description={t('empty.no_data.description', 'No se encontraron clientes en el sistema.')}
          />
        ) : showTable ? (
          <div className={`transition-opacity duration-300 ${isFetching ? 'opacity-60' : 'opacity-100'}`}>
            <CustomerTable
              customers={customers}
              paginationMeta={paginationMeta}
              isLoading={isLoading}
              isFetching={isFetching}
              onPaginationChange={updatePagination}
              onSortChange={updateSort}
              currentSort={{ column: sortColumn, direction: sortDirection }}
            />
          </div>
        ) : (
          <EmptyView
            type="noResults"
            icon={EMPTY_VIEW_ICONS.ORDERS}
            title={t('customerTable.emptyState.title', 'No se encontraron clientes')}
            description={t(
              'customerTable.emptyState.description',
              'No hay clientes que coincidan con los filtros seleccionados.',
            )}
          />
        )}
      </div>
    </SectionBox>
  );
};
